import React, { ReactElement, useEffect, useRef, useState } from 'react';

import cn from 'classnames';
import { Tab } from '@headlessui/react';
import { useRouter } from 'next/router';
import { Image, Icon, Spinner, IconButton } from 'components';
import { Tooltip } from 'components/Tooltip/Tooltip.component';
import { Toggle, Button } from 'components/form';
import { ButtonV3 } from 'components/ComponentV2';
import { Row } from 'components/layout';

import SlideShow from 'components/SlideShow';
// import { AddSubscribers } from 'pages/dashboard/AddSubscribers/AddSubscribers.component';
import { useSocietyProfile } from 'pages/society-profile/UseSocietyProfile.hook';
import useChartData from 'pages/dashboard/useGraphDataParser';
import DashboardDataBreakPointChart from 'pages/dashboard/DashboardDataBreakPointGraph';
import { AddSubscribers } from 'pages/dashboard/AddSubscribers/AddSubscribers.component';
import { withAuth } from 'lib/hocs/withAuth';
import { useCommunityInfo } from 'lib/contexts/community-context';
import { CommunityTypes, CommunityAttributes } from 'lib/models/community';
import { PAGE_ROUTES } from 'lib/page-routes';
import SubscribersTable from '../CommunityTable';
import CommunityStatusGraph from '../CommunityStatusGraph';
import CommunityScreeningModalWrapper from '../CommunityScreeningModal/CommunityScreeningModal.component';
import { useCommunityBoard } from '../useCommunityBoard';

import InviteMembersModal from '../InviteMembersConfirmationModal';
import { DisableRequireApprovalModal } from '../DisableRequireApprovalModal';
import styles from './Community.module.scss';

const Community = (): ReactElement => {
  const router = useRouter();
  const MIN_FILLED_RATIO = 0.15;

  const useBottomRef = useRef<HTMLDivElement | null>(null);
  const startTableRef = useRef<HTMLDivElement | null>(null);

  const {
    societyProfile,
    updateSubscriberApproval,
    updateInviteBannerStatus,
    addNewSubscriber,
    inviteAttendees,
    inviteProfileViewers,
    inviteEventViewers,
    id,
    sendFillDataEmail,
  } = useSocietyProfile();
  const [showSendEmailNotificationStub, setShowSendEmailNotificationStub] =
    useState(true);

  const OnSendInvite = async () => {
    await sendFillDataEmail();
    setShowSendEmailNotificationStub(false);
  };

  const {
    subscribers_require_approval = false,
    invite_later_banner_hidden = true,
    fill_data_email_sent_at,
  } = societyProfile || {};

  const {
    dashboardData,
    mutateCommunityDashboard,
    onApproveSubscriptionRequests,
    onDeclineSubscriptionRequests,
    onDeleteMember,
    onCSVExport,
    sendMessage,
  } = useCommunityBoard();

  const { subscription_requests_count = 0, attendees_count = 0 } =
    dashboardData || {};

  const {
    applications,
    size,
    setSize,
    hasMoreRecords,
    isLoading,
    isLoadingMore,
    mutate,
    applicationsMetaInfo,
    selectedCommunityType,
    onChangeCommunityType,
  } = useCommunityInfo();

  const [selectedState, setSelectedState] = useState(0);
  const [showBanner, setShowBanner] = useState(true);

  const [selectedRows, setSelectedRows] = useState<Array<string>>([]);

  const [selectedApplicantId, setSelectedApplicantId] = useState<string>('');
  const [showCommunityScreening, setShowCommunityScreening] =
    useState<boolean>(false);

  const [showInviteConfirmationModal, setShowInviteConfirmationModal] =
    useState<boolean>(false);
  const [isSingleMemberModal, setIsSingleMemberModal] =
    useState<boolean>(false);

  const SubriberStateOption = [
    ...(subscribers_require_approval && subscription_requests_count > 0
      ? [
          {
            id: '1',
            value: CommunityTypes.SUBSCRIPTION_REQUESTS,
            label: 'Membership requests',
          },
        ]
      : []),
    {
      id: '2',
      value: CommunityTypes.SUBSCRIBED,
      label: 'Members',
    },
    {
      id: '3',
      value: CommunityTypes.UNSUBSCRIBED,
      label: 'Past members',
    },
    {
      id: '4',
      value: CommunityTypes.INVITED,
      label: 'Invited',
    },
    {
      id: '5',
      value: CommunityTypes.ATTENDEES,
      label: 'Attendees',
    },
  ];

  useEffect(() => {
    if (
      subscription_requests_count &&
      selectedCommunityType === CommunityTypes.SUBSCRIPTION_REQUESTS
    ) {
      mutate();
    }
    if (selectedCommunityType === CommunityTypes.ATTENDEES) {
      setShowBanner(true);
    }
  }, [subscription_requests_count, selectedCommunityType]);

  const { objectToArrayWithTopAndOthers, memberDetailsArray } =
    useChartData(id);
  useEffect(() => {
    if (
      (!subscribers_require_approval || subscription_requests_count === 0) &&
      selectedCommunityType === CommunityTypes.SUBSCRIPTION_REQUESTS
    ) {
      onChangeCommunityType(CommunityTypes.SUBSCRIBED);
      setSelectedState(0);
      mutate();
    } else if (selectedCommunityType) {
      onChangeCommunityType(selectedCommunityType);
      const selectedIndex = SubriberStateOption.findIndex(
        (option) => option.value === selectedCommunityType
      );
      if (selectedIndex !== -1) {
        setSelectedState(selectedIndex);
      }
      mutate();
    } else {
      setDefaultCommunityType();
    }
  }, [
    selectedCommunityType,
    subscribers_require_approval,
    subscription_requests_count,
  ]);

  function isMoreThan30DaysApart(inputDateString: string) {
    const inputDate = new Date(inputDateString);
    const currentDate = new Date();
    const timeDifference = inputDate.getTime() - currentDate.getTime();
    const millisecondsIn30Days = 30 * 24 * 60 * 60 * 1000;
    return timeDifference > millisecondsIn30Days;
  }

  useEffect(() => {
    if (fill_data_email_sent_at) {
      setShowSendEmailNotificationStub(
        isMoreThan30DaysApart(fill_data_email_sent_at)
      );
    }
  }, [fill_data_email_sent_at]);

  const toggleCommunityScreeningMode = (openScreeningMode: boolean) => () => {
    setShowCommunityScreening(openScreeningMode);
    if (!openScreeningMode) {
      setSelectedApplicantId('');
    }
  };

  const hasRatioLessThanOrEqualToMinFilledRatio = () => {
    for (const element of memberDetailsArray) {
      if (
        element?.value?.members_with_details_count / element?.value?.total <=
        MIN_FILLED_RATIO
      ) {
        return true;
      }
    }
    return false;
  };

  const selectAll = () => {
    if (selectedRows.length === applications.length) {
      setSelectedRows([]);
    } else {
      const tempList = applications.map(
        (application: CommunityAttributes) => application.id
      );
      setSelectedRows(tempList);
    }
  };

  const handleSelectRows = (selected: boolean, id: string) => {
    setSelectedRows((prevRows) => {
      const updatedSelectedRows = selected
        ? [...prevRows, id]
        : prevRows.filter((rowId) => rowId !== id);
      return updatedSelectedRows;
    });
  };

  const getEmptyCommunityContent = (option: string) => {
    switch (option) {
      case CommunityTypes.SUBSCRIPTION_REQUESTS:
        return 'No membership requests yet';
      case CommunityTypes.SUBSCRIBED:
        return 'No members yet';
      case CommunityTypes.UNSUBSCRIBED:
        return 'No unsubscribed members yet';
      case CommunityTypes.INVITED:
        return 'No one invited to your community yet';
      case CommunityTypes.ATTENDEES:
        return 'No attendees yet';
      default:
        return '';
    }
  };
  const emptyCommunityContent = () => {
    return (
      <section className={styles.CommunityEmptyContainer}>
        <Image
          src={'/images/empty-state.png'}
          className={styles.CommunityEmptyContainerImg}
        />
        <p className={styles.CommunityEmptyContainerHeading}>
          {getEmptyCommunityContent(selectedCommunityType)}
        </p>
      </section>
    );
  };
  const CommunityTableLayout = ({
    children,
    hasMoreRecords,
    setSize,
    size,
    total,
    currentCount,
    isLoading,
  }: {
    children: React.ReactNode;
    hasMoreRecords: boolean;
    setSize: (size: number) => void;
    size: number;
    total: number;
    currentCount: number;
    isLoading?: boolean;
  }) => {
    return (
      <div
        className={cn(styles.tableLayout, {
          [styles.tableLayoutHoverState]: total,
        })}
      >
        {total || isLoading ? children : emptyCommunityContent()}
        {total ? (
          <div className={styles.tableLayoutFooter}>
            <Row align="center" className={styles.countsRow}>
              <p>
                Showing {hasMoreRecords ? currentCount : total} of {total}
              </p>
              {hasMoreRecords && (
                <Button
                  variant="text"
                  className={styles.countsButton}
                  onClick={() => setSize(size + 1)}
                >
                  Show more
                </Button>
              )}
            </Row>
          </div>
        ) : null}
      </div>
    );
  };

  const onInviteAttendee = async (attendee: {
    first_name: string;
    last_name: string;
    email: string;
  }) => {
    setIsSingleMemberModal(true);
    try {
      await addNewSubscriber(attendee);
    } catch (error) {
      console.error(error);
    }
  };

  const onInviteAttendees = async () => {
    setIsSingleMemberModal(false);
    try {
      await inviteAttendees();
    } catch (error) {
      console.error(error);
    }
  };
  const toggleCommunityStatus = async () => {
    if (subscribers_require_approval) {
      const res = await DisableRequireApprovalModal();
      if (res) {
        await updateSubscriberApproval(!subscribers_require_approval);
        await mutateCommunityDashboard();
      }
    } else {
      await updateSubscriberApproval(!subscribers_require_approval);
    }
  };

  const onInviteEventViewers = async () => {
    setIsSingleMemberModal(false);
    try {
      await inviteEventViewers();
    } catch (error) {
      console.error(error);
    }
  };

  const onInviteProfileViewers = async () => {
    setIsSingleMemberModal(false);
    try {
      await inviteProfileViewers();
    } catch (error) {
      console.error(error);
    }
  };

  const onViewDashboard = () => {
    if (
      selectedCommunityType === CommunityTypes.EVENT_VIEWS ||
      selectedCommunityType === CommunityTypes.PROFILE_VIEWS
    ) {
      setDefaultCommunityType();
    }
    toggleCommunityScreeningMode(false)();
  };

  const setDefaultCommunityType = () => {
    if (subscribers_require_approval && subscription_requests_count > 0) {
      onChangeCommunityType(CommunityTypes.SUBSCRIPTION_REQUESTS);
      setSelectedState(0);
    } else {
      onChangeCommunityType(CommunityTypes.SUBSCRIBED);
      setSelectedState(0);
    }
  };

  const onScrollBottom = () => {
    startTableRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  return (
    <>
      <section className={styles.Community} data-testid="Community">
        <Row
          align="center"
          columnGap={4}
          className={styles.redirect}
          onClick={() => router.push(PAGE_ROUTES.DASHBOARD)}
        >
          <Icon iconName="chevron-left" size="large" />
          Dashboard
        </Row>
        <div
          className={cn({
            [styles.header]: true,
          })}
        >
          <p className={styles.title}>Community</p>
          <div className={styles.CommunityInvite}>
            <div className={styles.CommunityInviteToggleSection}>
              <Tooltip
                content="Members will require approval to join to your community if they are not invited."
                position="bottom"
                xAlign="center"
                color="dark"
                className={cn(styles.tooltipContainer)}
                containerClassName={styles.tooltipContainerContent}
              >
                <Icon
                  iconName="icon_info"
                  className={styles.tooltipContainerIcon}
                  size="auto"
                />
              </Tooltip>

              <label>Require approval</label>
              <Toggle
                className={styles.CommunityToggleSectionButton}
                checked={subscribers_require_approval}
                onChange={toggleCommunityStatus}
              />
            </div>
            <div className={styles.CommunityInviteDropDownSection}>
              <AddSubscribers />
            </div>
          </div>
        </div>

        {subscribers_require_approval && subscription_requests_count > 0 && (
          <section className={styles.subscribersRequestAlertSection}>
            <Row
              align="center"
              justify="space-between"
              wrap={false}
              columnGap={12}
              className={styles.subscribersRequestAlertRow}
            >
              <Row columnGap={12} wrap={false} align="center">
                <div className={styles.subscribersRequestAlertIconCover}>
                  <Icon
                    iconName="invite-mail"
                    size="xlarge"
                    className={styles.subscribersRequestAlertIcon}
                  />
                </div>
                <div className={styles.subscribersRequestAlertText}>
                  <p className={styles.subscribersRequestAlertTextTitle}>
                    Membership request
                  </p>
                  <p className={styles.subscribersRequestAlertTextDesc}>
                    {subscription_requests_count === 1
                      ? `There is 1 new membership request awaiting your approval.`
                      : `There are ${subscription_requests_count} new membership requests awaiting your approval.`}
                  </p>
                </div>
              </Row>

              <Button
                color="white"
                className={styles.whitebutton}
                onClick={() => {
                  onChangeCommunityType(CommunityTypes.SUBSCRIPTION_REQUESTS);
                  onScrollBottom();
                }}
              >
                View
              </Button>
            </Row>
          </section>
        )}

        {subscription_requests_count === 0 &&
          !invite_later_banner_hidden &&
          attendees_count > 0 && (
            <section className={styles.subscribersRequestAlertSection}>
              <Row
                align="center"
                justify="space-between"
                wrap={false}
                columnGap={12}
                className={cn(styles.subscribersRequestAlertRow, styles.invite)}
              >
                <Row columnGap={12} wrap={false} align="center">
                  <div className={styles.subscribersRequestAlertIconCover}>
                    <Icon
                      iconName="invite-mail"
                      size="xlarge"
                      className={styles.subscribersRequestAlertIcon}
                    />
                  </div>
                  <div className={styles.subscribersRequestAlertText}>
                    <p className={styles.subscribersRequestAlertTextTitle}>
                      Invite your community to join
                    </p>
                    <p className={styles.subscribersRequestAlertTextDesc}>
                      Members can be engaged through newsletters and automated
                      event updates. Gain valuable analytics on your members to
                      share with companies and secure sponsorships.
                    </p>
                  </div>
                </Row>
                <Row columnGap={8} wrap={false} align="center">
                  <Button
                    color="white"
                    className={styles.whitebutton}
                    onClick={() => {
                      onInviteAttendees();
                      updateInviteBannerStatus(true);
                    }}
                  >
                    Invite now
                  </Button>
                  <Button
                    color="white"
                    className={styles.whitebutton}
                    onClick={() => updateInviteBannerStatus(true)}
                  >
                    I’ll do it later
                  </Button>
                </Row>
              </Row>
            </section>
          )}
        <div className={styles.CommunityContentCointer}>
          <div className={styles.CommunityStatusSectionGraph}>
            <CommunityStatusGraph
              dashboardData={dashboardData}
              viewStatDetail={(value) => {
                onChangeCommunityType(value);
                toggleCommunityScreeningMode(true)();
              }}
            />
          </div>
          <div className={styles.chartContainer}>
            <SlideShow
              slidesPerRow={3}
              gutter={15}
              maxWidth="280px"
              width={280}
              partialSlide={0.5}
              className={styles.sponsorsSlider}
              navigationClassName={styles.navigationButton}
            >
              {memberDetailsArray
                .filter((member) => member.value.members_with_details_count > 0)
                .map((member, index) => {
                  const {
                    value: { members_with_details_count, total, demographics },
                    title,
                  } = member;

                  return (
                    <div key={index}>
                      <DashboardDataBreakPointChart
                        title={title}
                        memberData={objectToArrayWithTopAndOthers(
                          4,
                          title,
                          demographics
                        )}
                        total={total}
                        membersWithDetails={members_with_details_count}
                      />
                    </div>
                  );
                })}
            </SlideShow>
          </div>

          {hasRatioLessThanOrEqualToMinFilledRatio() &&
            showSendEmailNotificationStub && (
              <div className={styles.notificationStubContainer}>
                <Icon iconName="icon_info" size="xmediumplus" />
                <div>
                  <p className={styles.notificationHeading}>
                    Be attractive to sponsors and employers
                  </p>
                  <p className={styles.notificationDesc}>
                    Invite your members to fill missing data to help them
                    standout more in front of recruiters and for your community
                    to become more attractive for sponsors
                  </p>
                </div>
                <div className={styles.sendButtonContianer}>
                  <ButtonV3
                    className={styles.sendButton}
                    size="medium"
                    onClick={OnSendInvite}
                  >
                    Send Invite
                  </ButtonV3>
                </div>
              </div>
            )}
          <div className={styles.subscribersListSection}>
            <Row
              align="center"
              justify="space-between"
              className={styles.subscribersListSectionHeader}
            >
              <div className={styles.SubcribersTab}>
                <Tab.Group
                  selectedIndex={selectedState}
                  onChange={(index) => {
                    setSize(1);
                    setSelectedState(index);
                    onChangeCommunityType(SubriberStateOption[index].value);
                  }}
                >
                  <Tab.List className={styles.tabList}>
                    {SubriberStateOption.map((tab, index) => {
                      return (
                        <React.Fragment key={index}>
                          {index !== 0 &&
                          index !== selectedState &&
                          index !== selectedState + 1 ? (
                            <span className={styles.seperator}></span>
                          ) : null}
                          <Tab
                            key={tab.id}
                            className={({ selected }) =>
                              cn(
                                styles.tab,
                                { [styles.tabSelected]: selected },
                                {
                                  [styles.tabDisabled]: false,
                                }
                              )
                            }
                          >
                            {tab.label}
                          </Tab>
                        </React.Fragment>
                      );
                    })}
                  </Tab.List>
                </Tab.Group>
              </div>
              <Row className={styles.subscribersListSectionHeaderBtnRow}>
                <ButtonV3
                  className={styles.subscribersListSectionHeaderBtn}
                  color="secondary"
                  size="small"
                  disabled={applications?.length === 0}
                  onClick={() => {
                    if (selectedCommunityType) {
                      onCSVExport(selectedCommunityType as CommunityTypes);
                    }
                  }}
                >
                  Export CSV
                </ButtonV3>
                <ButtonV3
                  size="small"
                  startIcon="icon_arrows-maximize-2"
                  className={styles.subscribersListSectionHeaderBtn}
                  startIconClassName={
                    styles.subscribersListSectionHeaderBtnIcon
                  }
                  disabled={
                    selectedCommunityType === CommunityTypes.UNSUBSCRIBED ||
                    selectedCommunityType === CommunityTypes.INVITED ||
                    applications?.length === 0
                  }
                  onClick={() => {
                    toggleCommunityScreeningMode(true)();
                  }}
                >
                  View more
                </ButtonV3>
              </Row>
            </Row>
            {selectedCommunityType === CommunityTypes.ATTENDEES && showBanner && (
              <div className={styles.bannerText}>
                <p>
                  <Icon iconName="icon_info" />
                  These are attendees who haven’t joined your community yet.
                </p>
                <IconButton
                  onClick={() => setShowBanner(false)}
                  iconName="icon_close"
                  className={styles.icon}
                />
              </div>
            )}
            <div className={styles.content}>
              <section
                className={styles.CandidatesInfo}
                data-testid="CandidatesInfo"
              >
                <div ref={startTableRef}></div>
                <CommunityTableLayout
                  hasMoreRecords={hasMoreRecords}
                  setSize={setSize}
                  size={size}
                  total={applicationsMetaInfo.total || 0}
                  currentCount={applications?.length || 0}
                  key={selectedCommunityType}
                  isLoading={isLoading || isLoadingMore}
                >
                  <SubscribersTable
                    key={selectedCommunityType}
                    isLoading={isLoading}
                    rows={applications}
                    communityType={selectedCommunityType}
                    setIsAllRowsSelected={selectAll}
                    isAllRowsSelected={
                      applications.length > 0 &&
                      selectedRows.length === applications.length
                    }
                    selectedRowsIds={selectedRows}
                    setSelectedRows={(...params) => handleSelectRows(...params)}
                    clearSelectRows={() => setSelectedRows([])}
                    showCheckbox={
                      selectedCommunityType ===
                      CommunityTypes.SUBSCRIPTION_REQUESTS
                    }
                    refetch={mutate}
                    reftechDashboard={mutateCommunityDashboard}
                    onOpenScreeningModal={() =>
                      toggleCommunityScreeningMode(true)()
                    }
                    handleApplicationSelect={(id: string) => {
                      setSelectedApplicantId(id);
                    }}
                    onApproval={onApproveSubscriptionRequests}
                    onRejection={onDeclineSubscriptionRequests}
                    onInviteAttendee={onInviteAttendee}
                    onSendMessage={sendMessage}
                    onDelete={onDeleteMember}
                  />
                  {!isLoading && isLoadingMore ? (
                    <Spinner
                      size="small"
                      className={styles.spinner}
                      wrapperClassname={styles.spinnerWrapper}
                      wrapper
                    />
                  ) : null}
                </CommunityTableLayout>
              </section>
            </div>
          </div>
        </div>
        {showCommunityScreening ? (
          <CommunityScreeningModalWrapper
            selectedApplicationId={selectedApplicantId}
            selectedCommunityStatus={selectedCommunityType}
            isOpen={showCommunityScreening}
            viewDashboard={onViewDashboard}
            onInviteAttendee={onInviteAttendee}
            inviteAttendees={onInviteAttendees}
            inviteProfileViewers={onInviteProfileViewers}
            inviteEventViewers={onInviteEventViewers}
            onSendMessage={sendMessage}
            subscribers_require_approval={subscribers_require_approval}
            subscription_requests_count={subscription_requests_count}
          />
        ) : null}

        <InviteMembersModal
          isSingleMember={isSingleMemberModal}
          open={showInviteConfirmationModal}
          onClose={() => setShowInviteConfirmationModal(false)}
        />

        <div ref={useBottomRef} className={styles.bottomElement}></div>
      </section>
    </>
  );
};

const CommunityWrapper = withAuth(Community);

export default CommunityWrapper;
